import type { Session, Ticket } from "./types";

export const NOMINATION_FEE_PER_CHECKIN = 100;

export interface FeePayout {
  session: Session;
  performer: string;
  amountPaid: number;
}

export interface AfternoonFeeRow {
  performer: string;
  count: number;
  owed: number;
  paid: number;
}

export interface EveningFeeRow {
  performer: string;
  eveningCount: number;
  afternoonCount: number;
  unpaidAfternoon: number;
  eveningOwed: number;
  totalOwed: number;
  paid: number;
}

export interface FeeSummaryRow {
  performer: string;
  afternoonPaid: number;
  eveningPaid: number;
  totalPaid: number;
}

function checkedNominationCounts(tickets: Ticket[]): Record<Session, Map<string, number>> {
  const counts: Record<Session, Map<string, number>> = {
    afternoon: new Map(),
    evening: new Map(),
  };
  for (const ticket of tickets) {
    for (const unit of ticket.units || []) {
      if (!unit.checkedIn) continue;
      if (!unit.performer || unit.performer === "尚未指名") continue;
      const map = counts[unit.session];
      map.set(unit.performer, (map.get(unit.performer) || 0) + 1);
    }
  }
  return counts;
}

function paidMap(payouts: FeePayout[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const payout of payouts) {
    map.set(`${payout.session}:${payout.performer}`, Math.max(0, Number(payout.amountPaid) || 0));
  }
  return map;
}

export function buildAfternoonFeeRows(tickets: Ticket[], payouts: FeePayout[]): AfternoonFeeRow[] {
  const counts = checkedNominationCounts(tickets).afternoon;
  const paid = paidMap(payouts);
  return [...counts.entries()]
    .map(([performer, count]) => ({
      performer,
      count,
      owed: count * NOMINATION_FEE_PER_CHECKIN,
      paid: paid.get(`afternoon:${performer}`) || 0,
    }))
    .sort((a, b) => b.count - a.count || a.performer.localeCompare(b.performer, "zh-Hant"));
}

export function buildEveningFeeRows(tickets: Ticket[], payouts: FeePayout[]): EveningFeeRow[] {
  const counts = checkedNominationCounts(tickets);
  const paid = paidMap(payouts);
  const performers = new Set([...counts.evening.keys(), ...counts.afternoon.keys()]);
  return [...performers]
    .map((performer) => {
      const eveningCount = counts.evening.get(performer) || 0;
      const afternoonCount = counts.afternoon.get(performer) || 0;
      if (eveningCount === 0 && afternoonCount === 0) {
        return null;
      }
      // Evening table focuses on groups that have evening check-ins,
      // or afternoon unpaid that may be settled at evening.
      const afternoonOwed = afternoonCount * NOMINATION_FEE_PER_CHECKIN;
      const afternoonPaid = paid.get(`afternoon:${performer}`) || 0;
      const unpaidAfternoon = Math.max(0, afternoonOwed - afternoonPaid);
      const eveningOwed = eveningCount * NOMINATION_FEE_PER_CHECKIN;
      if (eveningCount === 0 && unpaidAfternoon === 0) return null;
      return {
        performer,
        eveningCount,
        afternoonCount,
        unpaidAfternoon,
        eveningOwed,
        totalOwed: eveningOwed + unpaidAfternoon,
        paid: paid.get(`evening:${performer}`) || 0,
      };
    })
    .filter((row): row is EveningFeeRow => Boolean(row))
    .sort(
      (a, b) =>
        b.eveningCount - a.eveningCount ||
        b.unpaidAfternoon - a.unpaidAfternoon ||
        a.performer.localeCompare(b.performer, "zh-Hant"),
    );
}

export function buildFeeSummary(tickets: Ticket[], payouts: FeePayout[]): FeeSummaryRow[] {
  const afternoon = buildAfternoonFeeRows(tickets, payouts);
  const evening = buildEveningFeeRows(tickets, payouts);
  const performers = new Set([
    ...afternoon.map((row) => row.performer),
    ...evening.map((row) => row.performer),
  ]);
  const afternoonPaid = new Map(afternoon.map((row) => [row.performer, row.paid]));
  const eveningPaid = new Map(evening.map((row) => [row.performer, row.paid]));
  return [...performers]
    .map((performer) => {
      const a = afternoonPaid.get(performer) || 0;
      const e = eveningPaid.get(performer) || 0;
      return {
        performer,
        afternoonPaid: a,
        eveningPaid: e,
        totalPaid: a + e,
      };
    })
    .filter((row) => row.totalPaid > 0 || afternoon.some((item) => item.performer === row.performer) || evening.some((item) => item.performer === row.performer))
    .sort((a, b) => b.totalPaid - a.totalPaid || a.performer.localeCompare(b.performer, "zh-Hant"));
}
